import { sortBy } from "lodash-es";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";

import { ISelectProps, Select } from "@/components/Select";
import { Text } from "@/components/Text";
import { fetchCodelist } from "@/data/codelists";
import { ITranslatedLanguagesCodelist } from "@/data/languages";
import { codelistFactory, ICodelistFactoryProps as ICodelistProps, ReactSelectComponents } from "@/eworkitComponents";
import { withFormikField } from "@/formikComponents";
import { useCodelistTranslation } from "@/hooks/Codelist";

import { ICodelistGenericSelectParams } from "./CodelistsGeneric";

export interface IGroupedLanguages {
  label?: string;
  options: ITranslatedLanguagesCodelist[];
}

const LANGUAGES_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "languages" };

interface IGroupedLanguagesSelectProps
  extends ICodelistProps<Omit<ISelectProps, "filterAfterFetch" | "simpleValue" | "filterOption">, IGroupedLanguages> {
  filterLanguages?: string[];
}

const fetchLanguages = async () => {
  const options = await fetchCodelist(LANGUAGES_CODELIST_QUERY);

  return [{ options }] as IGroupedLanguages[];
};

const CodelistGenericSelectComponent = codelistFactory<ISelectProps, IGroupedLanguages>(fetchLanguages, Select);

export const GroupedLanguagesSelect: FC<IGroupedLanguagesSelectProps> = ({
  onChange,
  value,
  filterLanguages = [],
  ...props
}) => {
  const tCodelist = useCodelistTranslation();
  const { t } = useTranslation("common");

  const p = {
    ...props,
    portalPlacement: "auto",
  };

  const groupAndTranslateOptions = ([{ options }]: IGroupedLanguages[]) => {
    const filteredOptions = options.filter(o => !filterLanguages.includes(o.code));

    const [top, rest] = filteredOptions.reduce(
      (prev: ITranslatedLanguagesCodelist[][], curr) => {
        const index = curr.parameter.topLanguage ? 0 : 1;
        prev[index] = [...prev[index], { ...curr, value: tCodelist(LANGUAGES_CODELIST_QUERY.codelistName, curr.code) }];
        return prev;
      },
      [[], []],
    );

    return [
      {
        label: t("COMPONENTS.LANGUAGES.TOP"),
        options: sortBy(top, "value"),
      },
      {
        label: t("COMPONENTS.LANGUAGES.REST"),
        options: sortBy(rest, "value"),
      },
    ];
  };

  const handleChange = (newValue: ITranslatedLanguagesCodelist) => {
    if (onChange) {
      onChange(newValue.code);
    }
  };

  const handleChangeMulti = (newValues?: ITranslatedLanguagesCodelist[]) => {
    if (onChange) {
      onChange(newValues?.map(v => v.code));
    }
  };

  const mapSingleValue = (languageCode: string) => ({ code: languageCode });

  return (
    <CodelistGenericSelectComponent
      filterAfterFetch={groupAndTranslateOptions}
      components={{
        MultiValueLabel: ({ children, ...restProps }) => {
          return (
            <ReactSelectComponents.MultiValueLabel {...restProps}>
              {tCodelist(LANGUAGES_CODELIST_QUERY.codelistName, restProps.data.code)}
            </ReactSelectComponents.MultiValueLabel>
          );
        },
        SingleValue: ({ children, ...restProps }) => {
          return (
            <ReactSelectComponents.SingleValue {...restProps}>
              <Text size={restProps.selectProps.size}>
                {tCodelist(LANGUAGES_CODELIST_QUERY.codelistName, restProps.data.code)}
              </Text>
            </ReactSelectComponents.SingleValue>
          );
        },
      }}
      simpleValue={false}
      value={value ? (p.isMulti ? value.map(mapSingleValue) : mapSingleValue(value)) : null}
      onChange={p.isMulti ? handleChangeMulti : handleChange}
      {...p}
    />
  );
};

export const GroupedLanguagesSelectFormik = withFormikField(GroupedLanguagesSelect);
